import type { UiTheme } from "../theme";
import type { ChartVariant } from "./chartAnnotations";

// PG-31/PG-33 — pure colour/marker logic split out of `WagmiLineChart.tsx`
// (the 500-line cap). Renderer-agnostic: neither function touches wagmi or
// React, both are provable without mounting a chart. `lineColor` isn't a
// hypothetical split either — it already had a caller outside this chart
// (`TierOneCompareRow.tsx`, PG-26) before this file existed.

export type DotAppearance = { color: string; size: number; borderColor?: string; borderWidth?: number };

/** What `dotAppearance`/`neutralDotAppearance` need to know about a drawn
 *  layer — narrower than `WagmiLineChart`'s own `Layer` (no `id`: a
 *  colour/marker decision never depends on WHICH series it is, only on what
 *  is true about it). */
export type DotLayer = { variant: ChartVariant; current: boolean; stale: boolean };

// Exported for PG-26: the compare caption paints each exercise name in its own
// curve's colour, and a legend that computes its own colours is a legend that
// can lie. One source, so a swatch can never show a colour the line does not.
export function lineColor(theme: UiTheme, variant: ChartVariant) {
  if (variant === "focal") return theme.palette.brand500;
  if (variant === "secondary") return theme.palette.ink3;
  return theme.palette.ink;
}

/** How a line is drawn. #389: curves of one colour told apart by this.
 *  `dashed` is the secondary curve's short dash; `longDash` the tracker's. */
export type LineStroke = "solid" | "dashed" | "longDash" | "dotted" | "dashDot";

/** The curve's own stroke when it names one, else its variant's. The line
 *  and its legend swatch (LineSwatch) both read it here. */
export function lineStroke(variant: ChartVariant, stroke?: LineStroke): LineStroke {
  return stroke ?? (variant === "secondary" ? "dashed" : "solid");
}

/** The stroke width, in the theme's pixels (`k`, pictureScale). */
export function lineWidth(variant: ChartVariant, k: number) {
  return (variant === "secondary" ? 1.25 : 2.5) * k;
}

/** The dash tokens, in points before `k`: the one source of every pattern.
 *  The long dash is over twice the short one and the dots' gap is wide, so
 *  at 320 a long dash, a dotted and a dash-dot line cannot be taken for one
 *  another on a 28 pt swatch. `dot`: a zero-length dash, drawn by the round
 *  cap as a dot as wide as the line. */
export const LINE_DASH = {
  short: 3,
  shortGap: 1.75,
  long: 8,
  longGap: 3.5,
  dot: 0.01,
  dotGap: 5,
  dashDot: 6,
  dashDotGap: 3,
} as const;

/** The SVG dash pattern of a stroke at `width`. Round caps grow each dash by
 *  the width, so each gap adds it back. A secondary line keeps its 3,3. */
export function lineDashArray(stroke: LineStroke, width: number, k: number): string | undefined {
  const d = LINE_DASH;
  if (stroke === "dashed") return `${d.short * k},${d.shortGap * k + width}`;
  if (stroke === "longDash") return `${d.long * k},${d.longGap * k + width}`;
  if (stroke === "dotted") return `${d.dot},${d.dotGap * k + width}`;
  if (stroke === "dashDot") {
    const gap = d.dashDotGap * k + width;
    return `${d.dashDot * k},${gap},${d.dot},${gap}`;
  }
  return undefined;
}

/** The widest dot drawn: the stale one, below. */
export const LARGEST_DOT_SIZE = 5;

// "Now" is a claim about time, so it is EARNED by being recent — never by
// being last, and never by default (HM-1, 2026-07-15). A stale last point
// stays VISIBLE but goes hollow: it is the newest reading we have, so hiding
// it would be a second lie — it just stops claiming to be the present.
export function dotAppearance(theme: UiTheme, layer: DotLayer): DotAppearance {
  if (layer.variant === "secondary") {
    return { color: theme.palette.ink3, size: 2.5 };
  }
  if (layer.variant === "focal") {
    return { color: theme.palette.brand500, size: 3.5 };
  }
  const isNowDot = layer.current;
  const isStaleDot = layer.stale;
  if (isStaleDot) {
    return {
      color: theme.palette.surface,
      size: LARGEST_DOT_SIZE,
      borderColor: theme.palette.ink,
      borderWidth: 2.5,
    };
  }
  return {
    color: isNowDot ? theme.palette.brand500 : theme.palette.ink,
    size: isNowDot ? 4 : 3,
  };
}

/** #389: the last dot in its line's own colour, for curves of one colour
 *  where an orange "now" would single one curve out. A stale last point
 *  still goes hollow: that claim is about time, not about the curve. */
export function lineDotAppearance(theme: UiTheme, layer: DotLayer): DotAppearance {
  const accent = dotAppearance(theme, layer);
  return layer.variant === "primary" && !layer.stale
    ? { ...accent, color: lineColor(theme, layer.variant) }
    : accent;
}

// Every non-accent point (i.e. every point except the series' last one —
// `dotAppearance` above) gets the SAME small neutral marker regardless of
// variant: the accent is the one claim about a point worth spending colour
// on, everything else just marks "a sample existed here" (old SVG renderer
// parity, per-point dots). ⛔ This used to say "the last point of the
// series' LAST SEGMENT", which stopped being a thing at PG-36.
export function neutralDotAppearance(theme: UiTheme): DotAppearance {
  return { color: theme.palette.ink3, size: 2 };
}
